import { useEffect, useState } from 'react';
import axiosInstance from '../../axiosConfig';

// Au plus 4 miniatures chargées en même temps, pour ne pas occuper les workers
// du back avec une page entière de vignettes.
export const MAX_PARALLEL_THUMBNAILS = 4;
// Délais entre deux demandes quand la miniature n'est pas encore prête (202) :
// au-delà (~30 s), on abandonne et le front garde l'icône du type.
export const THUMBNAIL_RETRY_DELAYS = [1000, 2000, 3000, 5000, 8000, 13000];

let running = 0;
const waiting = [];

const runNext = () => {
  if (running >= MAX_PARALLEL_THUMBNAILS || waiting.length === 0) return;
  const { task, resolve, reject, signal } = waiting.shift();
  if (signal.aborted) {
    reject(new DOMException('Aborted', 'AbortError'));
    runNext();
    return;
  }
  running += 1;
  task()
    .then(resolve, reject)
    .finally(() => {
      running -= 1;
      runNext();
    });
};

const limited = (task, signal) =>
  new Promise((resolve, reject) => {
    waiting.push({ task, resolve, reject, signal });
    runNext();
  });

const wait = (delay, signal) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, delay);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });

const fetchThumbnail = async (path, signal) => {
  for (let attempt = 0; ; attempt += 1) {
    const response = await limited(
      () =>
        axiosInstance.get('/media_library/thumbnail/', {
          params: { path },
          responseType: 'blob',
          signal,
        }),
      signal
    );
    if (response.status !== 202) return response.data;
    if (attempt >= THUMBNAIL_RETRY_DELAYS.length) return null;
    await wait(THUMBNAIL_RETRY_DELAYS[attempt], signal);
  }
};

// Miniature d'un fichier de la bibliothèque, chargée seulement si `enabled`
// (vignette visible). Renvoie son URL locale, ou `unavailable` si le back n'en
// produit pas (format, erreur, délai dépassé) : on affiche alors l'icône.
export const useMediaThumbnail = (path, { enabled = true } = {}) => {
  const [state, setState] = useState({ url: null, unavailable: false });

  useEffect(() => {
    if (!path || !enabled) return undefined;
    const controller = new AbortController();
    let url = null;
    fetchThumbnail(path, controller.signal)
      .then((blob) => {
        if (!blob) {
          setState({ url: null, unavailable: true });
          return;
        }
        url = URL.createObjectURL(blob);
        setState({ url, unavailable: false });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setState({ url: null, unavailable: true });
        }
      });
    return () => {
      controller.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [path, enabled]);

  return state;
};
